import { useEffect, useCallback } from 'react';

interface UseEscapeModalOptions {
  isOpen: boolean;
  onClose: () => void;
  hasUnsavedChanges?: boolean;
  confirmMessage?: string;
}

/**
 * Custom hook for handling ESC key to close modals with confirmation for unsaved changes
 */
export const useEscapeModal = ({
  isOpen,
  onClose,
  hasUnsavedChanges = false,
  confirmMessage = 'You have unsaved changes. Are you sure you want to close without saving?'
}: UseEscapeModalOptions) => {
  const handleEscapeKey = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      
      if (hasUnsavedChanges) {
        // Show confirmation dialog for unsaved changes
        const confirmed = window.confirm(confirmMessage);
        if (confirmed) {
          onClose();
        }
      } else {
        // Close immediately if no unsaved changes
        onClose();
      }
    }
  }, [isOpen, hasUnsavedChanges, confirmMessage, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isOpen, handleEscapeKey]);
};

/**
 * Utility function to check if form data has changed from initial values
 * Works with objects by doing a shallow comparison
 */
export const hasFormChanges = <T extends Record<string, any>>(
  currentData: T,
  initialData: T,
  fieldsToIgnore: (keyof T)[] = []
): boolean => {
  for (const key in currentData) {
    if (fieldsToIgnore.includes(key)) continue;
    
    const currentValue = currentData[key];
    const initialValue = initialData[key];
    
    // Handle arrays
    if (Array.isArray(currentValue) && Array.isArray(initialValue)) {
      if (currentValue.length !== initialValue.length) return true;
      // For simple arrays, do JSON comparison
      if (JSON.stringify(currentValue) !== JSON.stringify(initialValue)) return true;
    }
    // Handle objects (but not arrays or null)
    else if (currentValue && typeof currentValue === 'object' && !Array.isArray(currentValue)) {
      if (!initialValue || typeof initialValue !== 'object') return true;
      if (JSON.stringify(currentValue) !== JSON.stringify(initialValue)) return true;
    }
    // Handle primitives
    else if (currentValue !== initialValue) {
      return true;
    }
  }
  return false;
}; 